import { Button, Descriptions, Table, Tree } from "antd";
import React from "react";
import * as Icons from '@ant-design/icons';
import { SaveOutlined } from '@ant-design/icons';
import { BaseComponent } from "../../components/infrastructure/baseComponent";
import { enmEntityState, ICallOptions } from "../../components/infrastructure/models";
import { DataNode } from "antd/lib/tree";
import { Core } from "../../components/infrastructure/core";
import { IUserClaim, IUserClaimGetResult, IUserClaimProps, IUserClaimStates } from "./interfaces";
import { IClaim, IRoleClaim } from "../roleClaim/interfaces";
import { IRole } from "../role/interfaces";
import { IUserRole } from "../userRole/interfaces";
import CustomSpinner from "../../components/infrastructure/customSpinner";

export class UserClaim extends BaseComponent<IUserClaimProps, IUserClaimStates>{

    constructor(props: IUserClaimProps) {
        super(props)
        this.state = {
            isAuthorized: true,
            loading: true,
            userRoles: [],
            userClaims: [],
            roleClaims: [],
            roles: [],
            selectedRoles: [],
            claims: [],
            expandedKeys: [],
            allowEdit: false,
            checkedKeys: {
                checked: [],
                halfChecked: []
            },
            windowSize: window.innerWidth <= 765 ? 1 : 2
        };
    }
    componentDidMount() {
        super.componentDidMount();
        this.loadData();
    }
    async loadData() {
        if (!this.props.params || !this.props.params().user?.Id)
            return;

        let opt: ICallOptions = {
            url: 'user/getuserclaims',
            data: { userId: this.props.params && this.props.params().user?.Id },
            onSuccess: (result) => {
                if (result.ValidationState.Successful) {
                    let data: IUserClaimGetResult = (result as any).Data;
                    let selectedRoles: React.Key[] = data.UserRoles.map(r => r.Role.RowGuid);
                    let claims = data.Claims.map(c => (c.ParentName ? c.ParentName + "." : "") + c.Name);
                    let checkedKeys: React.Key[] = data.RoleClaims.filter(r => claims.indexOf(r.Claim) > -1).map(r => r.Claim);
                    data.UserClaims.filter(r => claims.indexOf(r.Claim) > -1).forEach(r => checkedKeys.push(r.Claim));

                    let expandedKeys: React.Key[] = [];
                    checkedKeys.forEach(k => {
                        if (expandedKeys.indexOf(k) === -1)
                            expandedKeys.push(k);
                    });

                    if (this.mounted) {
                        this.setState({
                            loading: false,
                            userRoles: data.UserRoles,
                            userClaims: data.UserClaims,
                            roleClaims: data.RoleClaims,
                            roles: data.Roles,
                            claims: data.Claims,
                            selectedRoles: selectedRoles,
                            expandedKeys: expandedKeys,
                            allowEdit: data.AllowEdit,
                            checkedKeys: { checked: checkedKeys, halfChecked: [] }
                        });
                    }
                }
            }
        };
        await this.callApi(opt);
    }
    getIcon = (name?: string) => {
        const Icon = name ? (Icons as any)[name] : null;
        if (Icon)
            return <Icon />;
    }
    renderClaims = (claims: IClaim[], parentName: string | null = null): DataNode[] => {
        let nodes: DataNode[] = [];
        claims.filter(c => c.ParentName === parentName)
            .forEach(c => {
                if (c.Name === "Home" || c.Name === "Portal")
                    return;
                let key = (c.ParentName ? c.ParentName + "." : "") + c.Name;
                let node: DataNode = {
                    key: key,
                    title: c.Title,
                    disableCheckbox: !!this.state.roleClaims.find(r => r.Claim === key),
                    icon: this.getIcon(c.Icon)
                };
                let children = this.renderClaims(claims, c.Name);
                if (children.length)
                    node.children = children;
                nodes.push(node);
            });
        return nodes;
    }
    onExpand = (expandedKeysValue: React.Key[]) => {
        this.setState({ expandedKeys: expandedKeysValue });
    }
    onCheck = (checked: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[]; }) => {
        if ((checked as any)?.checked) {
            let checkedKeys = checked as { checked: React.Key[]; halfChecked: React.Key[]; };
            let currentCheckedKeys: React.Key[] = [];
            checkedKeys.checked.forEach(key => {
                let name: string | null = key as string;
                while (name) {
                    let tmp: string | null = name?.split('.')[0];
                    let node = this.state.claims.map(c => { return { ParentName: c.ParentName, Name: c.Name } })
                        .find(c => c.Name === tmp);

                    if (node && checkedKeys.checked.indexOf(node.Name) === -1 && currentCheckedKeys.indexOf(node.Name) === -1) {
                        name = (node.ParentName ? node.ParentName + "." : "") + node.Name;
                        if (name.split('.').length === 1)
                            break;
                        if (currentCheckedKeys.indexOf(name) === -1)
                            currentCheckedKeys.push(name);
                    }
                    else
                        name = null;
                }
                if (currentCheckedKeys.indexOf(key) === -1)
                    currentCheckedKeys.push(key);
            });
            this.setState({
                checkedKeys: { checked: currentCheckedKeys, halfChecked: [] }
            });
        }
    }
    async save() {
        let userClaims: IUserClaim[] = [];
        let userRoles: IUserRole[] = [];
        this.state.userClaims.forEach(item => {
            if (this.state.checkedKeys.checked.indexOf(item.Claim) === -1 && !this.state.roleClaims.find(r => r.Claim === item.Claim))
                userClaims.push({ Id: item.Id, RowGuid: item.RowGuid, RowVrsn: item.RowVrsn, State: enmEntityState.Deleted } as any);
        });
        this.state.checkedKeys.checked.forEach(item => {
            if (this.state.userClaims.map(i => i.Claim).indexOf(item.toString()) === -1 && !this.state.roleClaims.find(r => r.Claim === item.toString()))
                userClaims.push({ RowGuid: Core.newId(), State: enmEntityState.Added, UserId: this.props.params && this.props.params().user?.Id, Claim: item.toString() } as any);
        });

        this.state.userRoles.forEach(item => {
            if (this.state.selectedRoles.indexOf(item.Role.RowGuid) === -1)
                userRoles.push({ Id: item.Id, RowGuid: item.RowGuid, RowVrsn: item.RowVrsn, State: enmEntityState.Deleted } as any);
        });
        this.state.selectedRoles.forEach(item => {
            if (this.state.userRoles.map(i => i.Role.RowGuid).indexOf(item.toString()) === -1) {
                let role = this.state.roles.find(u => u.RowGuid === item.toString());
                userRoles.push({ RowGuid: Core.newId(), State: enmEntityState.Added, UserId: this.props.params && this.props.params().user?.Id, RoleId: role?.Id } as any);
            }
        });

        if (userClaims.length || userRoles.length) {
            let opt: ICallOptions = {
                url: 'user/saveuserclaims',
                data: {
                    changes: {
                        UserClaims: userClaims,
                        UserRoles: userRoles
                    }
                },
                onSuccess: async (result) => {
                    if (result.ValidationState.Successful) {
                        Core.successNotification('تغییرات با موفقیت ذخیره شد');
                        await this.loadData();
                    }
                }
            };
            await this.callApi(opt);
        }
    }
    onSelectedRolesChanged = async (selectedRowKeys: React.Key[], selectedRows: IRole[]) => {
        let roleIds = selectedRows.map(r => r.Id);
        let opt: ICallOptions = {
            url: 'role/getclaims',
            data: { roleIds: roleIds ? roleIds : [] },
            onSuccess: async (result) => {
                if (result.ValidationState.Successful) {
                    let roleClaims: IRoleClaim[] = (result as any).Data;
                    let claims = this.state.claims.map(c => (c.ParentName ? c.ParentName + "." : "") + c.Name);
                    let checkedKeys: React.Key[] = roleClaims.filter(r => claims.indexOf(r.Claim) > -1).map(r => r.Claim);
                    this.state.userClaims.filter(r => claims.indexOf(r.Claim) > -1).forEach(r => checkedKeys.push(r.Claim));

                    let expandedKeys = this.state.expandedKeys;
                    checkedKeys.forEach(k => {
                        if (expandedKeys.indexOf(k) === -1)
                            expandedKeys.push(k);
                    });
                    if (this.mounted) {
                        this.setState({
                            selectedRoles: selectedRowKeys,
                            roleClaims: roleClaims,
                            expandedKeys: expandedKeys,
                            checkedKeys: { checked: checkedKeys, halfChecked: [] }
                        });
                    }
                }
            }
        };
        await this.callApi(opt);
    }
    virtualRender() {

        const nodes: DataNode[] = this.renderClaims(this.state.claims);
        let expandedKeys: React.Key[] = this.state.expandedKeys;
        nodes.forEach(c => {
            if (expandedKeys.indexOf(c.key) === -1)
                expandedKeys.push(c.key);
        });

        let extraButtons: React.ReactNode;
        if (this.state.allowEdit)
            extraButtons = (
                <div key="userclaims-extraButtons">
                    <Button key="userclaims-1" type="ghost" onClick={() => {
                        if (this.props.onClose)
                            this.props.onClose(this);
                        else
                            window.history.back();
                    }} icon={this.getIcon("StopOutlined")} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: '100px', marginLeft: 10, background: '#fff' }}>
                        لغو
                    </Button>
                    <Button key="userclaims-2" style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: '100px' }} type="primary" onClick={() => this.save()} icon={<SaveOutlined />} loading={this.state.loading}>
                        ذخیره
                    </Button>
                </div>
            );

        let descriptions: React.ReactNode | undefined =
            this.props.params &&
            (
                <Descriptions key="userclaims-description" className="header-page">
                    <Descriptions.Item key="userclaims-username" label="نام کاربری">{this.props.params().user?.UserName}</Descriptions.Item>
                    <Descriptions.Item key="userclaims-fullname" label="نام و نام خانوادگی">{this.props.params().user?.FullName}</Descriptions.Item>
                </Descriptions>
            );

        const header = this.getPageHeader({
            title: "دسترسی های کاربران",
            onBack: () => {
                if (this.props.onClose)
                    this.props.onClose(this);
                else
                    window.history.back();
            },
            extraHeaderButtons: extraButtons,
            extraHeaderNodes: descriptions
        });

        return (
            <>
                <div style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    {header}
                </div>
                <div key="userclaims-container" className="row" style={{ margin: 0, marginTop: 10 }}>
                    <div key="userclaims-grid" className="col-md-4"
                        style={{
                            margin: 0,
                            padding: 0,
                            paddingBottom: this.state.windowSize === 1 ? 10 : 0
                        }}>
                        {!this.state.loading &&
                            <Table
                                rowKey="RowGuid"
                                rowSelection={{
                                    type: 'checkbox',
                                    selectedRowKeys: this.state.selectedRoles,
                                    onChange: this.onSelectedRolesChanged
                                }}
                                pagination={{
                                    style: { marginLeft: 10 },
                                    size: "small"
                                }}
                                dataSource={this.state.roles}
                                className="box"
                                columns={[
                                    {
                                        dataIndex: "Description",
                                        title: "نقش"
                                    }
                                ]}
                            />
                        }
                    </div>
                    <div key="userclaims-tree" className="col-md-8"
                        style={{
                            margin: 0,
                            padding: 0,
                            paddingRight: this.state.windowSize === 1 ? 0 : 10
                        }}>
                        <div >
                            {this.state.loading
                                ? <div className="center" style={{ background: "#fff" }}><CustomSpinner /></div>
                                :
                                <Tree
                                    className="box"
                                    showLine={true}
                                    showIcon={true}
                                    checkable
                                    expandedKeys={expandedKeys}
                                    checkedKeys={this.state.checkedKeys}
                                    onExpand={this.onExpand}
                                    onCheck={this.onCheck}
                                    treeData={nodes}
                                    checkStrictly={true}
                                    style={{ margin: 10 }}
                                />}
                        </div>
                    </div>
                </div>
            </>
        );
    }
}